import { Link } from "react-router-dom";
import { ConcernList, TreatmentIndexNav, TreatmentSection } from "@/pages/treatment/components/TreatmentSections";
import { FlowSteps } from "@/pages/treatment/components/TreatmentProcess";
import { TreatmentCta, TreatmentFaq } from "@/pages/treatment/components/TreatmentFaqCta";
import TreatmentPriceLink from "@/pages/treatment/components/TreatmentPriceLink";
import SubTreatmentHero from "./SubTreatmentHero";
import type { SubTreatmentPage } from "@/mocks/subTreatmentPages";

/**
 * 個別施術詳細ページの共通テンプレート。
 * NOTE: 未確定の医療情報（施術時間・ダウンタイム・麻酔・特徴・方法・リスク等）は、
 *       推測して作成せず、患者向け画面にも表示しない。
 *       確定済みの情報（お悩み・対応部位・料金・FAQ・CTA）のみを表示する。
 */

const flowSteps = [
  { no: "01", title: "ご予約", text: "WEB予約・LINE・お電話より、ご希望の日時をお知らせください。" },
  { no: "02", title: "カウンセリング", text: "お悩みやご希望、生活スタイルを丁寧にお伺いします。" },
  { no: "03", title: "医師の診察", text: "状態を確認し、適した方法をご提案します。" },
  { no: "04", title: "施術", text: "内容・料金・リスクについてご説明し、ご納得のうえで施術を行います。" },
  { no: "05", title: "アフターケア", text: "施術後の経過を確認し、ホームケアをご案内します。" },
];

const faqItems = [
  {
    q: "カウンセリングだけでも相談できますか？",
    a: "はい。施術を決めていない段階でのご相談も歓迎しています。",
  },
  {
    q: "料金はどこで確認できますか？",
    a: "料金は料金表ページでご確認いただけます。",
  },
  {
    q: "予約の変更・キャンセルはできますか？",
    a: "前日までにご連絡いただければ承ります。",
  },
];

export default function SubTreatmentDetail({ data }: { data: SubTreatmentPage }) {
  const indexItems = [
    { label: "このようなお悩みの方へ", target: "sp-concerns" },
    ...(data.anchorNav ? [{ label: "対応部位", target: "sp-anchor" }] : []),
    { label: "施術の流れ", target: "sp-flow" },
    { label: "料金", target: "sp-price" },
    { label: "Q&A", target: "sp-faq" },
  ];

  return (
    <>
      <SubTreatmentHero data={data} />
      <TreatmentIndexNav items={indexItems} />

      <TreatmentSection
        id="sp-concerns"
        en="CONCERNS"
        jp="このようなお悩みの方へ"
        lead={`${data.categoryJp}ページでご案内しているお悩みです。`}
        tone="plain"
      >
        <ConcernList items={data.concerns} />
      </TreatmentSection>

      {data.anchorNav ? (
        <TreatmentSection
          id="sp-anchor"
          en={data.anchorNav.en}
          jp={data.anchorNav.jp}
          lead={data.anchorNav.lead}
          tone="tint"
        >
          <div className="flex flex-wrap gap-2.5">
            {data.anchorNav.items.map((item) => (
              <Link
                key={item.target}
                to={`${data.categoryPath}#${item.target}`}
                className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-[12.5px] text-foreground-800 transition-colors hover:border-accent-400 hover:bg-secondary-100/70 hover:text-foreground-950"
              >
                {item.label}
                <i
                  className="ri-arrow-right-line text-sm text-foreground-400 transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </Link>
            ))}
          </div>
        </TreatmentSection>
      ) : null}

      <TreatmentSection id="sp-flow" en="FLOW" jp="施術の流れ" tone="plain">
        <FlowSteps items={flowSteps} />
      </TreatmentSection>

      <TreatmentSection id="sp-price" en="PRICE" jp="料金" tone="tint">
        <TreatmentPriceLink categoryId={data.priceTab} categoryJp={data.categoryJp} />
      </TreatmentSection>

      <TreatmentSection id="sp-faq" en="Q&amp;A" jp="よくあるご質問" tone="plain">
        <div className="max-w-3xl">
          <TreatmentFaq items={faqItems} />
        </div>
        <div className="mt-12">
          <Link
            to={data.categoryPath}
            className="group inline-flex items-center gap-2 whitespace-nowrap text-[13px] text-foreground-800 transition-colors hover:text-accent-700"
          >
            <i className="ri-arrow-left-line transition-transform group-hover:-translate-x-0.5" aria-hidden />
            {data.backLabel}
          </Link>
        </div>
      </TreatmentSection>

      <TreatmentCta lineLabel={data.ctaLabel} treatmentJp={data.jp} />
    </>
  );
}